import { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/base/buttons'
import {
  useBusinessProfile,
  usePrefillBusinessProfile,
  useUpdateBusinessProfile,
  type BusinessProfile,
  type BusinessProfileUpdate,
} from '@/hooks/api/useWhatsAppSignup'

// Twilio's WhatsApp business verticals. Values are exactly what Twilio's API expects.
const VERTICAL_OPTIONS = [
  'Automotive',
  'Beauty, Spa and Salon',
  'Clothing and Apparel',
  'Education',
  'Entertainment',
  'Event Planning and Service',
  'Finance and Banking',
  'Food and Grocery',
  'Hotel and Lodging',
  'Medical and Health',
  'Non-profit',
  'Professional Services',
  'Public Service',
  'Restaurant',
  'Shopping and Retail',
  'Travel and Transportation',
  'Other',
]

interface BusinessProfileFormProps {
  assistantId: number
}

type Draft = { [K in keyof BusinessProfileUpdate]: string }

function toDraft(profile: BusinessProfile | undefined): Draft {
  return {
    about: profile?.about ?? '',
    address: profile?.address ?? '',
    description: profile?.description ?? '',
    vertical: profile?.vertical ?? '',
    logo_url: profile?.logo_url ?? '',
    email: profile?.email ?? '',
    website: profile?.website ?? '',
    additional_website: profile?.additional_website ?? '',
  }
}

function toPayload(draft: Draft, initial: Draft): Partial<BusinessProfileUpdate> {
  const payload: Partial<BusinessProfileUpdate> = {}
  ;(Object.keys(draft) as Array<keyof Draft>).forEach((key) => {
    if (draft[key] !== initial[key]) payload[key] = draft[key]
  })
  return payload
}

const inputCls =
  'w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800'
const labelCls = 'mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300'

export function BusinessProfileForm({ assistantId }: BusinessProfileFormProps) {
  const { data, isLoading, error } = useBusinessProfile(assistantId)
  const update = useUpdateBusinessProfile(assistantId)
  const prefill = usePrefillBusinessProfile(assistantId)

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<Draft>(toDraft(undefined))
  const [initial, setInitial] = useState<Draft>(toDraft(undefined))

  useEffect(() => {
    if (data) {
      const d = toDraft(data)
      setDraft(d)
      setInitial(d)
    }
  }, [data])

  const set = <K extends keyof Draft>(key: K, value: string) => setDraft((prev) => ({ ...prev, [key]: value }))

  const isDirty = (Object.keys(draft) as Array<keyof Draft>).some((k) => draft[k] !== initial[k])

  const handleSave = async () => {
    try {
      const payload = toPayload(draft, initial)
      if (Object.keys(payload).length === 0) return
      await update.mutateAsync(payload)
      toast.success('Business profile updated')
    } catch (e) {
      toast.error(`Update failed: ${e instanceof Error ? e.message : 'unknown'}`)
    }
  }

  return (
    <div className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between p-4 text-left"
      >
        <div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Business profile information</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Shown to users when they interact with Bonnie via WhatsApp.
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 flex-none text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
      <div className="border-t border-gray-200 p-4 dark:border-gray-700">
      {isLoading && <p className="text-xs text-gray-500">Loading…</p>}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400">
          Couldn't load profile: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      )}

      {data && (
        <>
          {draft.logo_url && (
            <img
              src={draft.logo_url}
              alt="Business logo"
              className="mt-4 h-16 w-16 rounded-full border border-gray-200 object-cover dark:border-gray-700"
              onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = 'none')}
            />
          )}

          <div className="mt-4 max-w-md">
            <label className={labelCls}>Business display name</label>
            <input
              type="text"
              value={data.business_display_name ?? ''}
              disabled
              className={`${inputCls} cursor-not-allowed opacity-60`}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              It's not possible to change the display name currently.
            </p>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className={labelCls}>Business address</label>
              <input type="text" value={draft.address} onChange={(e) => set('address', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Vertical</label>
              <select value={draft.vertical} onChange={(e) => set('vertical', e.target.value)} className={inputCls}>
                <option value="">Select a vertical…</option>
                {VERTICAL_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Business website</label>
              <input type="url" placeholder="https://" value={draft.website} onChange={(e) => set('website', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Additional website</label>
              <input type="url" placeholder="https://" value={draft.additional_website} onChange={(e) => set('additional_website', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Business email</label>
              <input type="email" value={draft.email} onChange={(e) => set('email', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Logo URL</label>
              <input
                type="url"
                placeholder="https://…/logo.png"
                value={draft.logo_url}
                onChange={(e) => set('logo_url', e.target.value)}
                className={inputCls}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Must point directly at a JPEG or PNG. Twilio fetches the URL and rejects other file types.
              </p>
            </div>
            <div>
              <label className={labelCls}>Business description ({draft.description.length}/512)</label>
              <textarea
                value={draft.description}
                onChange={(e) => set('description', e.target.value)}
                maxLength={512}
                rows={3}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Profile about ({draft.about.length}/139)</label>
              <textarea
                value={draft.about}
                onChange={(e) => set('about', e.target.value)}
                maxLength={139}
                rows={3}
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              type="button"
              color="primary"
              size="sm"
              isDisabled={!isDirty || update.isPending}
              isLoading={update.isPending}
              onPress={handleSave}
            >
              Save changes
            </Button>
            {isDirty && (
              <button
                type="button"
                onClick={() => setDraft(initial)}
                className="text-xs text-gray-500 underline-offset-2 hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-gray-100"
              >
                Discard
              </button>
            )}
            <Button
              type="button"
              color="secondary"
              size="sm"
              isLoading={prefill.isPending}
              onPress={async () => {
                try {
                  await prefill.mutateAsync()
                  toast.success('Business profile populated from assistant data')
                } catch (e) {
                  toast.error(`Populate failed: ${e instanceof Error ? e.message : 'unknown'}`)
                }
              }}
            >
              Populate from Bonnie data
            </Button>
          </div>
        </>
      )}
      </div>
      )}
    </div>
  )
}
